'use client';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { UseTimerReturn } from '../types/utils';

import { padNumberLeft } from './strings';

export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * 타이머를 관리하는 커스텀 훅입니다.
 * @param initMin
 * @param initSec
 * @param isStart
 *
 * @example
 * ```tsx
 * const { min, sec, restart, reset, isTimeOut } = useTimer(3, 0, true);
 * // 3분 0초에서 시작, 타이머가 동작함
 * ```
 */
export const useTimer = (initMin: number, initSec: number = 0, isStart: boolean = true): UseTimerReturn => {
  const initialMilliseconds = (initMin * 60 + initSec) * 1000;

  const [min, setMin] = useState(padNumberLeft(initMin, 2));
  const [sec, setSec] = useState(padNumberLeft(initSec, 2));
  const [restartState, setRestartState] = useState(new Date());
  const isTimeOut = min === '00' && sec === '00';

  const endTimeRef = useRef<number>(Date.now() + initialMilliseconds);
  const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const clearTimer = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = undefined;
    }
  };

  const restart = () => {
    endTimeRef.current = Date.now() + initialMilliseconds;
    setMin(padNumberLeft(initMin, 2));
    setSec(padNumberLeft(initSec, 2));
    setRestartState(new Date());
  };

  const reset = () => {
    endTimeRef.current = Date.now();
    setMin(padNumberLeft(0, 2));
    setSec(padNumberLeft(0, 2));
  };

  useEffect(() => {
    if (!isStart || isTimeOut) return clearTimer;
    interval.current = setInterval(() => {
      const remain = Math.max(0, Math.floor((endTimeRef.current - Date.now()) / 1000));
      setMin(padNumberLeft(Math.floor(remain / 60), 2));
      setSec(padNumberLeft(remain % 60, 2));
    }, 1000);

    return () => {
      clearTimer();
    };
  }, [isStart, isTimeOut, restartState]);

  return { min, sec, restart, reset, isTimeOut };
};
